import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Vault, LayoutDashboard, Wallet, PiggyBank, CalendarClock, FileWarning, Settings as SettingsIcon, LogOut, HelpCircle, Menu, X, ChevronsUpDown } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import CurrencySelector from "@/components/CurrencySelector";
import Onboarding from "@/components/Onboarding";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_TOP = [
  { to: "/dashboard", label: "Bóveda", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/cuentas", label: "Cuentas", icon: Wallet, testid: "nav-cuentas" },
];
const NAV_REPORTS = [
  { to: "/presupuesto", label: "Presupuesto", icon: PiggyBank, testid: "nav-budget" },
  { to: "/proximos-pagos", label: "Próximos pagos", icon: CalendarClock, testid: "nav-upcoming" },
  { to: "/pendientes-facturar", label: "Pendientes por facturar", icon: FileWarning, testid: "nav-invoices" },
];

function VaultSwitcher() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: vaults = [] } = useQuery({ queryKey: ["vaults"], queryFn: async () => (await api.get("/vaults")).data });
  const { data: invitations = [] } = useQuery({ queryKey: ["invitations"], queryFn: async () => (await api.get("/vaults/invitations")).data });
  const switchVault = async (vault_id) => {
    if (vault_id === user?.active_vault_id) return;
    const res = await api.post("/vaults/switch", { vault_id });
    setUser(res.data);
    qc.invalidateQueries();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="vault-switcher" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-secondary/40 hover:bg-accent transition-colors">
          <div className="relative w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <Vault className="w-4 h-4 text-emerald-500" />
            {invitations.length > 0 && <span data-testid="invite-badge" className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-emerald-500 text-[10px] font-bold text-white flex items-center justify-center">{invitations.length}</span>}
          </div>
          <span className="text-sm font-medium truncate flex-1 text-left">{user?.vault_name || "Bóveda"}</span>
          <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Bóvedas compartidas</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {vaults.map((v) => (
          <DropdownMenuItem key={v.vault_id} data-testid={`vault-option-${v.vault_id}`} onClick={() => switchVault(v.vault_id)} className="cursor-pointer">
            <Vault className="w-4 h-4 mr-2 text-emerald-500" />
            <span className="truncate flex-1">{v.name}</span>
            {v.active && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
          </DropdownMenuItem>
        ))}
        {invitations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="pending-invites-item" onClick={() => navigate("/configuracion")} className="cursor-pointer text-emerald-500">
              <Vault className="w-4 h-4 mr-2" />
              <span className="truncate flex-1">{invitations.length} invitación(es) pendiente(s)</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} data-testid={item.testid} onClick={() => setMobileOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
            isActive ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                     : "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent"}`
        }>
        <Icon className="w-[18px] h-[18px]" />
        {item.label}
      </NavLink>
    );
  };

  const Separator = () => <div className="my-3 border-t border-border" />;

  const UserFooter = () => (
    <div className="flex items-center gap-3 px-2 py-2 rounded-xl border border-border bg-secondary/40">
      {user?.picture ? (
        <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500 text-sm font-bold">{user?.name?.[0] || "U"}</div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{user?.name}</p>
        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
      </div>
      <NavLink to="/configuracion" data-testid="nav-settings" onClick={() => setMobileOpen(false)}
        title="Configuración" className="w-8 h-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-emerald-500">
        <SettingsIcon className="w-[18px] h-[18px]" />
      </NavLink>
      <button data-testid="logout-button" onClick={logout} title="Cerrar sesión"
        className="w-8 h-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-muted-foreground hover:text-rose-500">
        <LogOut className="w-[18px] h-[18px]" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background byto-grain">
      {showOnboarding && <Onboarding onFinish={() => setShowOnboarding(false)} />}

      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card/60 backdrop-blur-sm p-4">
        <div className="flex items-center gap-2.5 px-2 py-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center"><Vault className="w-5 h-5 text-emerald-500" /></div>
          <span className="text-xl font-bold tracking-tight">Byto</span>
        </div>
        <div className="mt-4"><VaultSwitcher /></div>
        <nav className="flex flex-col gap-1.5 mt-4">{NAV_TOP.map(navItem)}</nav>
        <Separator />
        <p className="px-3.5 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">Reportes</p>
        <nav className="flex flex-col gap-1.5">{NAV_REPORTS.map(navItem)}</nav>
        <div className="mt-auto"><Separator /><UserFooter /></div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-accent"><Menu className="w-5 h-5" /></button>
          <div className="flex items-center gap-2"><Vault className="w-5 h-5 text-emerald-500" /><span className="text-lg font-bold">Byto</span></div>
        </div>
        <div className="flex items-center gap-2">
          <CurrencySelector testId="currency-selector-mobile" />
          <ThemeToggle testId="theme-toggle-mobile" />
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-card border-r border-border p-4 flex flex-col animate-fade-in-up overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-2">
              <div className="flex items-center gap-2"><Vault className="w-5 h-5 text-emerald-500" /><span className="text-lg font-bold">Byto</span></div>
              <button onClick={() => setMobileOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-accent"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-3"><VaultSwitcher /></div>
            <nav className="flex flex-col gap-1.5 mt-4">{NAV_TOP.map(navItem)}</nav>
            <Separator />
            <p className="px-3.5 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">Reportes</p>
            <nav className="flex flex-col gap-1.5">{NAV_REPORTS.map(navItem)}</nav>
            <div className="mt-auto"><Separator /><UserFooter /></div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <div className="hidden lg:flex items-center justify-end gap-2 px-8 py-4">
          <CurrencySelector />
          <button data-testid="reopen-onboarding-button" onClick={() => setShowOnboarding(true)}
            className="w-10 h-10 rounded-xl border border-border bg-secondary/60 flex items-center justify-center hover:bg-accent transition-colors" aria-label="Ver introducción">
            <HelpCircle className="w-[18px] h-[18px] text-muted-foreground" />
          </button>
          <ThemeToggle />
        </div>
        <main className="px-4 sm:px-6 lg:px-8 pb-16 pt-4 lg:pt-0 max-w-6xl">{children}</main>
      </div>
    </div>
  );
}
